import type { Route } from '@tuyau/core/types';
import { effect, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AccountService } from '@core/auth';
import { TuyauService } from '@core/tuyau';
import { FinanceInvoiceService } from '@shared/data/finance-invoice.service';

export type Notification =
  Route.Response<'employee_v2.notification.index'>['data'][number];

export type NotificationType =
  | 'incident'
  | 'property-management-invoice'
  | 'finance-invoice';

export type NotificationFilter = {
  unreadOnly: boolean;
  type: NotificationType | null;
};

/** Same icons as the sidebar's search and task entries. */
export const NOTIFICATION_TYPES: readonly {
  value: NotificationType;
  label: string;
  icon: string;
}[] = [
  {
    value: 'incident',
    label: 'TYPE_INCIDENT',
    icon: 'wrench',
  },
  {
    value: 'property-management-invoice',
    label: 'TYPE_PROPERTY_MANAGEMENT_INVOICE',
    icon: 'building-2',
  },
  {
    value: 'finance-invoice',
    label: 'TYPE_FINANCE_INVOICE',
    icon: 'briefcase-business',
  },
];

/** Read off the link, not the tag: not every incident or invoice writer tags its rows. */
export function notificationIcon(notification: Notification): string {
  if (notification.hrefV2?.startsWith('/incidents/')) return 'wrench';
  if (notification.hrefV2?.startsWith('/property-management-invoices/'))
    return 'building-2';
  return notification.financeInvoiceId !== null ? 'briefcase-business' : 'bell';
}

/** Counts and mutations. Each view keeps its own list in a `NotificationList`. */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private tuyau = inject(TuyauService);
  private accountService = inject(AccountService);
  private financeInvoiceService = inject(FinanceInvoiceService);
  private router = inject(Router);

  private readonly _unreadCount = signal(0);
  private readonly _changes = signal(0);

  readonly unreadCount = this._unreadCount.asReadonly();
  /** Bumped after every mutation and push, so open lists refetch. */
  readonly changes = this._changes.asReadonly();

  constructor() {
    // Wait for authentication before fetching so the request includes a valid token.
    effect(() => {
      if (this.accountService.profile()) {
        this.reloadUnread();
      }
    });
  }

  fetch(filter: NotificationFilter, page: number, perPage: number) {
    return this.tuyau.client.api.employeeV2.notification.index({
      query: {
        status: filter.unreadOnly ? 'unread' : 'all',
        ...(filter.type && { type: filter.type }),
        page,
        perPage,
      },
    });
  }

  changed() {
    this._changes.update((n) => n + 1);
    void this.reloadUnread();
  }

  async reloadUnread() {
    try {
      const response = await this.fetch({ unreadOnly: true, type: null }, 1, 1);
      this._unreadCount.set(Number(response.metadata.total));
    } catch {
      // keep the last known count
    }
  }

  open(notification: Notification) {
    if (notification.financeInvoiceId !== null) {
      this.financeInvoiceService.reload();
    }
    if (notification.hrefV2) {
      this.router.navigateByUrl(notification.hrefV2);
    }
    if (!notification.readAt) {
      void this.setRead([notification.id], true);
    }
  }

  async setRead(ids: string[], read: boolean) {
    if (!ids.length) return;
    await this.tuyau.client.api.employeeV2.notification.update({
      body: { read, ids },
    });
    this.changed();
  }

  async delete(ids: string[]) {
    if (!ids.length) return;
    await this.tuyau.client.api.employeeV2.notification.destroy({
      body: { ids },
    });
    this.changed();
  }

  /** Mark-all and delete-all act on what the given filter shows. */
  async markAllAsRead(filter: NotificationFilter) {
    await this.setRead(
      await this.fetchAllIds({ ...filter, unreadOnly: true }),
      true
    );
  }

  async deleteAll(filter: NotificationFilter) {
    await this.delete(await this.fetchAllIds(filter));
  }

  private async fetchAllIds(filter: NotificationFilter): Promise<string[]> {
    const ids: string[] = [];
    // Cap at 100 pages (10 000 ids) to avoid runaway loops.
    for (let page = 1; page <= 100; page++) {
      const response = await this.fetch(filter, page, 100);
      ids.push(...response.data.map((n) => n.id));
      if (page >= Number(response.metadata.lastPage)) break;
    }
    return ids;
  }
}
